import type { FileTypeDefinition } from "./experience";
import type { CardCategoryDocument, CardTemplateDocument } from "./product";
import type { FieldDefinition } from "./templates";
import type { JsonObject, JsonValue, WorldDocument, WorldReference, EditPermission, ContextVisibility } from "./types";
import { emptyLayoutDocument } from "./layouts";
import { schemaField, fieldDefinitionSchema, mergeStructuredSchema, validateSchemaValue, type StructuredSchema } from "./structured-schema";
import { CRAFT_CORE_FIELDS } from "./craft-schemas";
export type FileTypeCategory = "game_master" | "game_system" | "world" | "tracking";
export interface RuntimeFileTypeDefinition extends FileTypeDefinition {
  sourceSchema?: StructuredSchema;
  /** Core compatibility types accept fields imported from legacy sessions. Creator types are strict. */
  allowAdditionalFields?: boolean;
  legacyIds?: string[];
  /** Broad classification for organizing file types in the registry. */
  category?: FileTypeCategory;
}
export interface TypedWorldFileInput {
  fileTypeId: string;
  fileId: string;
  storyId: string;
  title: string;
  summary?: string;
  fields: JsonObject;
  references?: WorldReference[];
  visibility?: WorldDocument["visibility"];
  createdAt: string;
}
export interface FileTypeValidationResult {
  ok: boolean;
  fields: JsonObject;
  errors: string[];
}
const SYSTEM_FIELD_IDS = new Set([
  "status", "source", "images", "triggers", "templateId", "templateVersion", "image", "imageGeneration",
  // Existing image worker envelope; these are not creator schema extensions.
  "imagePrompt", "imageRequestId", "movisImageRunId", "imageModel", "imageAspectRatio", "imageError",
  "id", "fileId", "storyId", "fileTypeId", "kind", "revision", "visibility", "displayVisibility", "createdAt", "updatedAt"
]);
const clone = <T>(value: T): T => structuredClone(value);
const stableSchema = (value: unknown): string => JSON.stringify(value, function (_key, entry) {
  return entry && typeof entry === "object" && !Array.isArray(entry)
    ? Object.fromEntries(Object.entries(entry).sort(([a], [b]) => a.localeCompare(b))) : entry;
});
/** Agent extensions cannot silently turn a compatible schema into a migration. */
function assertSchemaExtension(before: StructuredSchema, after: StructuredSchema, path = "schema"): void {
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (["title", "description", "properties", "required", "items"].includes(key)) continue;
    if (stableSchema(before[key]) !== stableSchema(after[key])) throw new Error(`${path}.${key} changes existing data rules; use an explicit creator migration`);
  }
  for (const key of after.required ?? []) if (!before.required?.includes(key)) throw new Error(`${path}.${key} must remain optional for existing files`);
  for (const [key, schema] of Object.entries(before.properties ?? {})) {
    if (!after.properties?.[key]) throw new Error(`${path}.${key} cannot be deleted by an automatic schema extension`);
    assertSchemaExtension(schema, after.properties[key]!, `${path}.${key}`);
  }
  const identity = (label: string) => label.normalize("NFKC").toLocaleLowerCase().replace(/[\s_\-]+/g, "");
  const names = new Map<string, string>();
  for (const [key, schema] of Object.entries(after.properties ?? {})) {
    for (const name of new Set([identity(key), identity(schema.title || key)])) {
      const previous = names.get(name);
      if (previous && previous !== key && (!before.properties?.[key] || !before.properties?.[previous])) throw new Error(`${path}.${key} duplicates ${previous}; reuse the existing field`);
      names.set(name, key);
    }
    if (!before.properties?.[key]) {
      const optional = (value: StructuredSchema): boolean => !value.required?.length && Object.values(value.properties ?? {}).every(optional) && (!value.items || optional(value.items));
      if (!optional(schema)) throw new Error(`${path}.${key} additions must be optional, including nested fields`);
    }
  }
  if (before.items) {
    if (!after.items) throw new Error(`${path}.items cannot be removed`);
    assertSchemaExtension(before.items, after.items, `${path}.items`);
  }
}
const isObject = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value));
function validateField(field: FieldDefinition, raw: unknown, path: string, allowAdditionalFields = false): { value?: JsonValue; errors: string[] } {
  const errors: string[] = [];
  if (raw == null || raw === "") {
    if (field.required && !allowAdditionalFields) errors.push(`${path} is required (type: ${field.type})`);
    return Object.hasOwn(field, "defaultValue") && !allowAdditionalFields ? { value: clone(field.defaultValue!), errors } : { errors };
  }
  if (field.type === "image") {
    if (typeof raw === "string" || (isObject(raw) && (typeof raw.url === "string" || typeof raw.assetId === "string" || isObject(raw.generation)))) return { value: clone(raw) as JsonValue, errors };
    errors.push(`${path} must be an image reference`);
  } else if (field.type === "text" || field.type === "long_text") {
    if (typeof raw !== "string") errors.push(`${path} must be text`);
    else return { value: raw, errors };
  } else if (field.type === "number") {
    if (typeof raw !== "number" || !Number.isFinite(raw)) errors.push(`${path} must be a finite number`);
    else return { value: raw, errors };
  } else if (field.type === "boolean") {
    if (typeof raw !== "boolean") errors.push(`${path} must be boolean`);
    else return { value: raw, errors };
  } else if (field.type === "enum") {
    if (!field.options?.some((option) => option === String(raw))) errors.push(`${path} is not an allowed option (accepted: ${field.options?.join(", ") ?? "none"})`);
    else return { value: raw as JsonValue, errors };
  } else if (field.type === "reference") {
    if (!(typeof raw === "string" || (Array.isArray(raw) && raw.every((item) => typeof item === "string")))) errors.push(`${path} must contain stable file ids`);
    else return { value: clone(raw) as JsonValue, errors };
  } else if (field.type === "list") {
    if (!Array.isArray(raw)) errors.push(`${path} must be a list`);
    else if (!field.item) return { value: clone(raw) as JsonValue, errors };
    else {
      const values: JsonValue[] = [];
      raw.forEach((item, index) => {
        const result = validateField({ ...field.item!, required: true }, item, `${path}[${index}]`);
        errors.push(...result.errors);
        if (result.value !== undefined) values.push(result.value);
      });
      if (!errors.length) return { value: values, errors };
    }
  } else if (field.type === "object") {
    if (!isObject(raw)) errors.push(`${path} must be an object`);
    else if (!field.properties?.length) return { value: clone(raw) as JsonValue, errors };
    else {
      const nested = validateFields(field.properties, raw, false);
      errors.push(...nested.errors.map((error) => `${path}.${error}`));
      if (!errors.length) return { value: nested.fields, errors };
    }
  }
  return { errors };
}
function validateFields(definitions: readonly FieldDefinition[], input: Record<string, unknown>, allowAdditionalFields: boolean): FileTypeValidationResult {
  const errors: string[] = [];
  const fields: JsonObject = {};
  const known = new Set(definitions.map((field) => field.id));
  let effectiveInput = input;
  if (!known.has("fields") && isObject(input.fields)) {
    const { fields: nested, ...rest } = input;
    effectiveInput = { ...(nested as Record<string, unknown>), ...rest };
  }
  for (const definition of definitions) {
    const raw = Object.hasOwn(effectiveInput, definition.id) ? effectiveInput[definition.id] : undefined;
    const result = validateField(definition, raw, definition.id, allowAdditionalFields);
    errors.push(...result.errors);
    if (definition.schema && definition.type !== "image" && definition.type !== "reference" && !result.errors.length) validateSchemaValue(definition.schema, result.value, definition.id, errors);
    if (result.value !== undefined) fields[definition.id] = result.value;
  }
  for (const [key, value] of Object.entries(effectiveInput)) {
    if (known.has(key)) continue;
    if (!allowAdditionalFields && !SYSTEM_FIELD_IDS.has(key)) errors.push(`${key} is not declared by the File Type (declared fields: ${[...known].join(", ")})`);
    else if (value !== undefined) fields[key] = clone(value) as JsonValue;
  }
  return { ok: errors.length === 0, fields, errors };
}
export class WorldFileTypeRegistry {
  readonly #types = new Map<string, RuntimeFileTypeDefinition>();
  readonly #designations = new Map<string, RuntimeFileTypeDefinition[]>();
  readonly #canonicalIds: string[] = [];
  constructor(definitions: readonly RuntimeFileTypeDefinition[] = []) {
    for (const definition of definitions) {
      if (this.#types.has(definition.id)) throw new Error(`Duplicate File Type id: ${definition.id}`);
      this.#types.set(definition.id, clone(definition));
      this.#canonicalIds.push(definition.id);
      for (const legacyId of definition.legacyIds ?? []) if (legacyId && !this.#types.has(legacyId)) this.#types.set(legacyId, clone(definition));
      const matches = this.#designations.get(definition.designation) ?? [];
      matches.push(clone(definition));
      this.#designations.set(definition.designation, matches);
    }
  }
  list(): RuntimeFileTypeDefinition[] { return this.#canonicalIds.map((id) => clone(this.#types.get(id)!)); }
  get(id: string): RuntimeFileTypeDefinition | undefined { const value = this.#types.get(id); return value ? clone(value) : undefined; }
  register(definition: RuntimeFileTypeDefinition): void {
    if (this.#types.has(definition.id)) throw new Error(`Duplicate File Type id: ${definition.id}`);
    this.#types.set(definition.id, clone(definition));
    this.#canonicalIds.push(definition.id);
    for (const legacyId of definition.legacyIds ?? []) if (legacyId && !this.#types.has(legacyId)) this.#types.set(legacyId, clone(definition));
    const matches = this.#designations.get(definition.designation) ?? [];
    matches.push(clone(definition));
    this.#designations.set(definition.designation, matches);
  }
  update(id: string, patch: Partial<RuntimeFileTypeDefinition>): RuntimeFileTypeDefinition {
    const existing = this.#types.get(id);
    if (!existing) throw new Error(`Unknown File Type: ${id}`);
    const sourceSchema = patch.sourceSchema ? mergeStructuredSchema(existing.sourceSchema ?? { type: "object", properties: Object.fromEntries(existing.fields.map(field => [field.id, fieldDefinitionSchema(field)])) }, patch.sourceSchema) : patch.fields ? { ...(existing.sourceSchema ?? {}), type: "object", properties: Object.fromEntries(patch.fields.map(field => [field.id, fieldDefinitionSchema(field)])), required: patch.fields.filter(field => field.required).map(field => field.id) } : existing.sourceSchema;
    const updated: RuntimeFileTypeDefinition = {
      ...existing,
      ...patch,
      id: existing.id,
      experienceId: existing.experienceId,
      ...(sourceSchema ? { sourceSchema } : {}),
      fields: sourceSchema?.properties ? Object.entries(sourceSchema.properties).map(([key, schema]) => ({ ...existing.fields.find(field => field.id === key), ...patch.fields?.find(field => field.id === key), ...schemaField(key, schema, sourceSchema.required?.includes(key)) })) : patch.fields ? clone(patch.fields) : existing.fields,
      layout: patch.layout ? clone(patch.layout) : existing.layout,
      version: existing.version + 1,
      updatedAt: patch.updatedAt ?? new Date().toISOString(),
    };
    this.#types.set(id, updated);
    for (const legacyId of existing.legacyIds ?? []) this.#types.set(legacyId, updated);
    this.#designations.set(existing.designation, (this.#designations.get(existing.designation) ?? []).map(type => type.id === id ? updated : type));
    return clone(updated);
  }
  updateFromAgent(id: string, patch: Partial<RuntimeFileTypeDefinition>): RuntimeFileTypeDefinition {
    const existing = this.get(id);
    if (!existing) throw new Error(`Unknown File Type: ${id}`);
    if (patch.fields && new Set(patch.fields.map(field => field.id)).size !== patch.fields.length) throw new Error("Duplicate field IDs; reuse the existing field");
    const preview = new WorldFileTypeRegistry([existing]).update(id, patch);
    for (const field of existing.fields) {
      const next = preview.fields.find(item => item.id === field.id);
      for (const key of ["editableBy", "visibleTo", "contextVisibility", "contextPriority", "referenceKinds"] as const) {
        if (stableSchema(field[key]) !== stableSchema(next?.[key])) throw new Error(`Field ${field.id} ${key} requires an explicit creator migration`);
      }
    }
    const schema = (definition: RuntimeFileTypeDefinition): StructuredSchema => definition.sourceSchema ?? {
      type: "object", properties: Object.fromEntries(definition.fields.map(field => [field.id, fieldDefinitionSchema(field)])),
      required: definition.fields.filter(field => field.required).map(field => field.id),
    };
    assertSchemaExtension(schema(existing), schema(preview));
    const content = (definition: RuntimeFileTypeDefinition) => {
      const { version: _version, updatedAt: _updatedAt, fields: _fields, sourceSchema: _schema, ...rest } = definition;
      return { ...rest, sourceSchema: schema(definition) };
    };
    if (stableSchema(content(existing)) === stableSchema(content(preview))) return existing;
    return this.update(id, patch);
  }
  resolveDocument(document: Pick<WorldDocument, "fileTypeId" | "kind">): RuntimeFileTypeDefinition | undefined {
    if (document.fileTypeId) return this.get(document.fileTypeId);
    const matches = this.#designations.get(document.kind) ?? [];
    return matches.length === 1 ? clone(matches[0]!) : matches.find((item) => item.id.startsWith("core.")) ? clone(matches.find((item) => item.id.startsWith("core."))!) : undefined;
  }
  canonicalize(document: WorldDocument): WorldDocument {
    const definition = this.resolveDocument(document);
    if (!definition) return clone(document);
    return { ...clone(document), fileTypeId: definition.id, schemaVersion: definition.version, kind: definition.designation };
  }
  validate(document: WorldDocument): FileTypeValidationResult {
    const definition = this.resolveDocument(document);
    if (!definition) return document.fileTypeId
      ? { ok: false, fields: clone(document.fields), errors: [`Unknown File Type: ${document.fileTypeId}`] }
      : { ok: true, fields: clone(document.fields), errors: [] };
    const result = validateFields(definition.fields, document.fields, Boolean(definition.allowAdditionalFields));
    if (document.kind !== definition.designation) result.errors.unshift(`kind must be ${definition.designation} for File Type ${definition.id}`);
    return { ...result, ok: result.errors.length === 0 };
  }
  create(input: TypedWorldFileInput): WorldDocument {
    const definition = this.get(input.fileTypeId);
    if (!definition) throw new Error(`Unknown File Type: ${input.fileTypeId}`);
    const validation = validateFields(definition.fields, input.fields, Boolean(definition.allowAdditionalFields));
    if (!validation.ok) throw new Error(`File Type validation failed: ${validation.errors.join("; ")}`);
    if (!input.fileId.trim() || !input.title.trim()) throw new Error("fileId and title are required");
    return {
      id: input.fileId,
      storyId: input.storyId,
      kind: definition.designation,
      fileTypeId: definition.id,
      schemaVersion: definition.version,
      revision: 1,
      title: input.title.trim(),
      summary: input.summary?.trim() ?? "",
      fields: validation.fields,
      references: clone(input.references ?? []),
      visibility: input.visibility ?? definition.contextPolicy.visibility,
      displayVisibility: definition.displayVisibility,
      editableBy: [...definition.editableBy],
      createdAt: input.createdAt,
      updatedAt: input.createdAt,
    };
  }
}
// Built-in compatibility types intentionally keep creator fields open. Engine
// state (location, time, scene status, conditions) lives in runtimeState and is
// changed only by deterministic tools, never by a File Type payload.
const coreFields: Record<string, FieldDefinition[]> = CRAFT_CORE_FIELDS;
export function coreFileTypeDefinitions(experienceId: string, at = "1970-01-01T00:00:00.000Z"): RuntimeFileTypeDefinition[] {
  const designationConfig: Record<string, { category?: FileTypeCategory; editableBy: EditPermission[]; visibility: ContextVisibility }> = {
    character:        { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "searchable" },
    npc:              { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "searchable" },
    item:             { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "searchable" },
    skill:            { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "searchable" },
    location:         { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "searchable" },
    condition:        { category: "tracking",     editableBy: ["creator", "gm", "system"],  visibility: "searchable" },
    faction:          { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "searchable" },
    quest:            { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "partial"    },
    scene:            { category: "tracking",     editableBy: ["creator", "gm", "system"],  visibility: "pinned"     },
    world_clock:      { category: "tracking",     editableBy: ["creator", "gm", "system"],  visibility: "pinned"     },
    rule:                     { category: "game_system",  editableBy: ["creator"],                  visibility: "searchable" },
    gm_instructions:          { category: "game_master",  editableBy: ["creator"],                  visibility: "pinned"     },
    optional_gm_instructions: { category: "game_master",  editableBy: ["creator"],                  visibility: "searchable" },
    game_start:               { category: "world",        editableBy: ["creator", "gm", "system"],  visibility: "pinned"     },
    epoch_checkpoint:         { category: "tracking",     editableBy: ["gm", "system"],             visibility: "partial"    },
  };
  return Object.entries(designationConfig).map(([designation, config]) => ({
    id: `core.${designation}`,
    experienceId,
    version: 1,
    name: designation.replaceAll("_", " "),
    description: `Built-in compatibility File Type for ${designation}`,
    designation,
    contentType: "json" as const,
    fields: coreFields[designation] ?? [],
    layout: emptyLayoutDocument(),
    contextPolicy: { visibility: config.visibility },
    displayVisibility: "player" as const,
    editableBy: config.editableBy,
    extensionIds: [],
    allowAdditionalFields: true,
    ...(config.category ? { category: config.category } : {}),
    createdAt: at,
    updatedAt: at,
  }));
}
function productField(field: CardTemplateDocument["fields"][number]): FieldDefinition {
  if (field.schema && typeof field.schema === "object") return schemaField(field.key, field.schema as StructuredSchema, field.required);
  if (field.type === "array") return { id: field.key, label: field.label, type: "list", required: field.required };
  if (field.type === "level" || field.type === "select") return { id: field.key, label: field.label, type: "enum", required: field.required, options: field.options.map((option) => String(option.value)), ...(Object.hasOwn(field, "defaultValue") ? { defaultValue: field.defaultValue as JsonValue } : {}) };
  if (field.type === "multi_select") return { id: field.key, label: field.label, type: "list", required: field.required, item: { id: `${field.key}_item`, label: field.label, type: "enum", options: field.options.map((option) => String(option.value)) }, ...(Object.hasOwn(field, "defaultValue") ? { defaultValue: field.defaultValue as JsonValue } : {}) };
  return {
    id: field.key,
    label: field.label,
    type: field.type === "stat_group" || field.type === "image" ? "object" : field.type,
    required: field.required,
    ...(Object.hasOwn(field, "defaultValue") ? { defaultValue: field.defaultValue as JsonValue } : {}),
  };
}
export function storyFileTypeDefinitions(
  experienceId: string,
  categories: readonly CardCategoryDocument[],
  templates: readonly CardTemplateDocument[],
): RuntimeFileTypeDefinition[] {
  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  return templates.filter((template) => template.enabled).map((template) => {
    const category = categoriesById.get(template.categoryId);
    const designation = template.designation.trim() || category?.name.trim().toLocaleLowerCase().replace(/[^\p{L}\p{N}_.:-]+/gu, "_") || "custom_file";
    return {
      id: category?.id ?? template.id,
      experienceId,
      version: template.version,
      name: template.name || category?.name || template.id,
      description: template.description || category?.description || "",
      designation,
      contentType: template.contentType,
      fields: template.fields.map(productField),
      ...(template.sourceSchema && typeof template.sourceSchema === "object" ? { sourceSchema: template.sourceSchema as StructuredSchema } : {}),
      layout: template.layout ?? emptyLayoutDocument(),
      ...(template.layouts ? { layouts: structuredClone(template.layouts) } : {}),
      contextPolicy: { visibility: template.contextVisibility },
      displayVisibility: category?.visibleToPlayers === false ? "creator" : "player",
      ...(typeof template.agentEditable === "boolean" ? { agentEditable: template.agentEditable } : {}),
      editableBy: template.agentEditable === false ? ["creator"] : ["creator", "gm", "system"],
      extensionIds: [],
      legacyIds: category && category.id !== template.id ? [template.id] : [],
      imagePolicy: { mode: "on_approval", styleInstructions: template.imageStyleInstruction },
      createdAt: new Date(template.updatedAt || 0).toISOString(),
      updatedAt: new Date(template.updatedAt || 0).toISOString(),
    };
  });
}
export function createStoryFileTypeRegistry(
  experienceId: string,
  categories: readonly CardCategoryDocument[] = [],
  templates: readonly CardTemplateDocument[] = [],
): WorldFileTypeRegistry {
  return new WorldFileTypeRegistry([...coreFileTypeDefinitions(experienceId), ...storyFileTypeDefinitions(experienceId, categories, templates)]);
}
